import * as Sentry from '@sentry/react';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import env from 'config';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { UserMsgType } from 'lib_ts/enums/sockets/users';
import { IWSMsg } from 'lib_ts/interfaces/i-machine-msg';
import { IMsgDataType } from 'lib_ts/interfaces/i-msg-data-type';
import { io, Socket } from 'socket.io-client';

const NOTIFY_WS_ERROR = false;

const VERBOSE = false;

let socket: Socket | null = null;

// will reuse an existing socket if it exists (rather than recreate a new one every time)
export const createSocket = (token: string): Socket => {
  if (socket) {
    // avoid recreation, make sure to disconnect before triggering create again
    if (VERBOSE) {
      console.debug('Reusing socket...');
    }
    return socket;
  }

  if (VERBOSE) {
    console.debug(`Creating socket w/ token: *${token.slice(-6)}`);
  }

  socket = io(env.ws_url, {
    path: env.ws_path,
    autoConnect: false,
    reconnection: true,
    transports: ['websocket'],
    auth: { token },
    secure: env.ws_url.startsWith('wss') || env.ws_url.startsWith('https'),
  });

  socket.on('pong', () => {
    console.debug('Socket pong received');
  });

  socket.connect();

  socket.emit('ping');

  socket.on('message', (message: IWSMsg) => {
    if (VERBOSE) {
      console.debug('Socket message received:', message);
    }

    if (!message.type) {
      console.error({
        event: 'WS received message with empty type, nothing dispatched',
        message,
      });
      return;
    }

    if (!message.sentryBaggage || !message.sentryTrace) {
      // cannot do sentry-related stuff, just dispatch the msg
      WebSocketHelper.dispatch(message.type, message.data);
      return;
    }

    // for tracing in sentry
    Sentry.continueTrace(
      {
        sentryTrace: message.sentryTrace,
        baggage: message.sentryBaggage,
      },
      () =>
        Sentry.startSpan(
          {
            name: `ws-react-receive: ${message.type}`,
            op: 'ws-react.receive',
          },
          () => WebSocketHelper.dispatch(message.type, message.data)
        )
    );
  });

  return socket;
};

export const getSocket = () => socket;

export const trySocketEmit = <T>(
  type: WsMsgType,
  msg: T,
  noRetry?: boolean
) => {
  if (!socket) {
    console.warn('Cannot emit via null socket', type, msg);
    return;
  }

  const message: IWSMsg = {
    type: type,
    data: msg as IMsgDataType,
  };

  if (noRetry) {
    socket.volatile.emit('message', message);
    return;
  }

  socket.emit('message', message);
};

export const destroySocket = () => {
  console.warn('Destroying socket...');

  socket?.disconnect();
  socket = null;
};

export const sendSocketMessage = (
  type: WsMsgType,
  data: any,
  source: string,
  retry = false
) => {
  try {
    if (!socket) {
      throw new Error(
        `Attempted to send ${type} message via null socket @ ${new Date().toISOString()} (${source}) `
      );
    }

    if (!retry && !socket.connected) {
      throw new Error(
        `Attempted to send ${type} message via disconnected socket @ ${new Date().toISOString()} (${source})`
      );
    }

    const traceData = Sentry.getTraceData();
    const sentryTrace = traceData['sentry-trace'];
    const sentryBaggage = traceData['baggage'];

    socket.emit('message', {
      type: type,
      data: data,
      sentryTrace,
      sentryBaggage,
    });

    return true;
  } catch (e) {
    console.error(e);

    if (NOTIFY_WS_ERROR) {
      NotifyHelper.warning({
        message_md:
          'There was a problem sending the WebSocket message. Please try again or refresh your browser.',
      });
    }

    return false;
  }
};

// for new method of sending messages without IWsMsg
export const sendSocket = <T, C = undefined>(
  type: UserMsgType,
  data: T,
  source: string,
  retry = false,
  callback?: C
) => {
  try {
    if (!socket) {
      throw new Error(
        `Attempted to send ${type} message via null socket @ ${new Date().toISOString()} (${source}) `
      );
    }

    if (!retry && !socket.connected) {
      throw new Error(
        `Attempted to send ${type} message via disconnected socket @ ${new Date().toISOString()} (${source})`
      );
    }

    const traceData = Sentry.getTraceData();
    const sentryTrace = traceData['sentry-trace'];
    const sentryBaggage = traceData['baggage'];

    socket.emit(
      type,
      {
        ...data,
        sentryTrace,
        sentryBaggage,
      },
      callback
    );

    return true;
  } catch (e) {
    console.error(e);

    if (NOTIFY_WS_ERROR) {
      NotifyHelper.warning({
        message_md:
          'There was a problem sending the WebSocket message. Please try again or refresh your browser.',
      });
    }

    return false;
  }
};
